import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, inputCls, labelCls } from "@/components/kit";
import { ArrowRight } from "lucide-react";

export default function SubmissionForm({ type, fields, submitLabel = "Envoyer", testid = "form", redirectOnSuccess }) {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const initial = fields.reduce((a, f) => ({ ...a, [f.name]: f.type === "multiselect" ? [] : "" }), {});
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onFirstFocus = () => {
    if (!started) { setStarted(true); track("form_start"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.showIf && !f.showIf(form)) continue;
      const v = form[f.name];
      if (f.required && (f.type === "multiselect" ? v.length === 0 : !v)) {
        toast.error(`Le champ « ${f.label} » est requis.`);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { type, ...form, source: getSource() };
      await api.post("/submissions", payload);
      track("form_submit");
      toast.success("Votre demande a bien été envoyée. Merci !");
      if (redirectOnSuccess) {
        navigate(redirectOnSuccess);
        return;
      }
      setForm(initial);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-lime-dim/40 bg-lime/10 p-10 text-center" data-testid={`${testid}-success`}>
        <h3 className="font-serif text-4xl text-ink">Merci !</h3>
        <p className="mt-3 text-ink/70">
          Votre demande a été transmise à notre équipe. Nous revenons vers vous très rapidement.
        </p>
        <button onClick={() => setDone(false)} className="mt-6 text-sm text-ink/60 underline hover:text-lime-dim">
          Envoyer une autre demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} onFocusCapture={onFirstFocus} className="space-y-7" data-testid={testid}>
      <div className="grid gap-7 sm:grid-cols-2">
        {fields.map((f) => {
          if (f.showIf && !f.showIf(form)) return null;
          const id = `${testid}-${f.name}`;
          return (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <label htmlFor={f.type === "multiselect" ? undefined : id} className={labelCls}>
                {f.label} {f.required && <span className="text-lime-dim" aria-hidden="true">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea id={id} rows={4} required={f.required || undefined} className={inputCls} data-testid={id} value={form[f.name]} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              ) : f.type === "multiselect" ? (
                <div className="flex flex-wrap gap-2" data-testid={id} role="group" aria-label={f.label}>
                  {f.options.map((o) => {
                    const active = form[f.name].includes(o);
                    return (
                      <button
                        type="button"
                        key={o}
                        aria-pressed={active}
                        data-testid={`${id}-${o.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setForm({ ...form, [f.name]: active ? form[f.name].filter((v) => v !== o) : [...form[f.name], o] })}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
              ) : f.type === "select" ? (
                <select id={id} required={f.required || undefined} className={`${inputCls} appearance-none`} data-testid={id} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}>
                  <option value="">Sélectionnez…</option>
                  {f.options.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              ) : (
                <input id={id} type={f.type || "text"} required={f.required || undefined} className={inputCls} data-testid={id} value={form[f.name]} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              )}
            </div>
          );
        })}
      </div>
      <button type="submit" disabled={loading} className={`${btnPrimary} disabled:opacity-60`} data-testid={`${testid}-submit`}>
        {loading ? "Envoi…" : submitLabel} <ArrowRight size={16} />
      </button>
      <p className="text-xs leading-relaxed text-ink/45" data-testid={`${testid}-privacy`}>
        En envoyant ce formulaire, vous acceptez que vos informations soient utilisées uniquement pour traiter votre demande. Vous disposez d'un droit d'accès, de rectification et de suppression — voir nos{" "}
        <Link to="/mentions-legales" className="underline hover:text-lime-dim">mentions légales</Link>.
      </p>
    </form>
  );
}
